import { z } from 'zod';
import { request, useMutation } from '@applyboard/ui-utils';
import { NewVasError } from '../common/errors/NewVasError';
import { getAccessToken } from '../common/utils/accessToken';
import { DEFAULT_MIN_STRING_LENGTH, FILE_NAME_MAX_LENGTH } from '../pages/RbcGicFormNew/constants';

const USER_FEEDBACK_V1_URL = '/api/vas_hub/v1/user_feedback';

const UserFeedBackSchema = z.object({
  email: z.string().optional(),
  feedback: z.string(),
  screenshots: z.array(
    z.object({
      file_name: z.string().min(DEFAULT_MIN_STRING_LENGTH).max(FILE_NAME_MAX_LENGTH),
      content: z.string(),
    }),
  ),
});

export type UserFeedBackType = z.infer<typeof UserFeedBackSchema>;

export function useUserSubmitFeedBack() {
  const { mutate, mutateAsync, isLoading, isError } = useMutation(
    async (userFeedback: UserFeedBackType) => {
      const token = await getAccessToken(false);
      return request(USER_FEEDBACK_V1_URL, {
        method: 'POST',
        authorization: token ? `Bearer ${token}` : undefined,
        schema: z.unknown(), // we don't care about the response here
        body: userFeedback,
        throwError: async (response: Response) => {
          const jsonMessage = await response.json();
          throw new NewVasError(jsonMessage, 'submit user feedback failed');
        },
      });
    },
  );

  const userFeedBack = (
    userFeedback: UserFeedBackType,
    onSuccess?: () => void,
    onError?: (error: unknown) => void,
  ) =>
    mutate(userFeedback, {
      onSuccess: () => onSuccess?.(),
      onError: (error: unknown) => onError?.(error),
    });

  const userFeedBackAsync = async (userFeedback: UserFeedBackType) => mutateAsync(userFeedback);

  return {
    userFeedBack,
    userFeedBackAsync,
    isUserFeedBackLoading: isLoading,
    isUserFeedBackError: isError,
  };
}
